import { Location } from '@angular/common';
import { NavigationStart, Router } from '@angular/router';

// Local screens participate in browser history just like routed screens. Keep data in
// memory: history contains only an opaque key, never a profile or an editor draft.
const entries = new Map<string, {scope: string; parent?: string; value: any}>();
let sequence = 0;

export class ScreenViewHistory<T> {
  private key: string;
  private stopPop: {unsubscribe(): void};
  private stopRouter: {unsubscribe(): void};

  constructor(private location: Location, private router: Router, private scope: string,
              private capture: () => T, private restore: (value: T) => void) {
    const restored = (location.getState() as any)?.nogoshView;
    if (entries.get(restored)?.scope === scope) {
      this.key = restored;
      this.restore(entries.get(restored).value);
    } else {
      this.key = this.create(this.capture());
      this.write(false);
    }
    this.stopRouter = router.events.subscribe(event => {
      if (event instanceof NavigationStart) this.save();
    });
    this.stopPop = location.subscribe(event => {
      const key = event.state?.nogoshView;
      if (entries.get(key)?.scope !== this.scope) return;
      this.save();
      this.key = key;
      this.restore(entries.get(key).value);
    });
  }

  push(value: T): void {
    this.save();
    this.key = this.create(value, this.key);
    this.write(true);
  }

  back(): boolean {
    if (!entries.has(entries.get(this.key)?.parent)) return false;
    this.save();
    this.location.back();
    return true;
  }

  destroy(): void {
    this.save();
    this.stopPop.unsubscribe();
    this.stopRouter.unsubscribe();
  }

  private save(): void {
    const entry = entries.get(this.key);
    if (entry) entry.value = this.capture();
  }

  private create(value: T, parent?: string): string {
    const key = `${Date.now()}-${++sequence}`;
    entries.set(key, {scope: this.scope, parent, value});
    if (entries.size > 150) entries.delete(entries.keys().next().value);
    return key;
  }

  private write(push: boolean): void {
    const state = {...this.location.getState() as object, nogoshView: this.key, ...(push ? {nogoshCanGoBack: true} : {})};
    const url = this.location.path(true);
    if (push) this.location.go(url, '', state);
    else this.location.replaceState(url, '', state);
  }
}
